import config from '../Config'
import { api } from '../Services/api'
import { readConsent, registerProps, setPersonProps } from './analytics'
import { rolloutBucket } from './sha256'
import { KEYS, readJSON, writeJSON } from './storage'

/* AdSense ads (ads spec, web/backend). The code ships, but until GET
   /v1/config turns ads on for this browser the page loads nothing of
   Google's: no adsbygoogle.js, no consent message (Google's CMP comes with
   that script), no slots. Without a config (never fetched) ads are off. */

const DEFAULT_TTL = 900
const PRODUCTION_HOSTS = ['instapres.com', 'www.instapres.com']

let ads = null // GET /v1/config "ads"
let fetchedAt = 0
let installId = ''
let bucket = -1
let eligible = false
let script = 'none' // none | loading | loaded | failed

const uuid = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  } catch (e) {
    // fall through
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/* A random id kept in localStorage (spec 3.2): rollout group, test browsers
   (testInstallIds) and analytics' distinct_id. Only once the visitor accepted
   cookies (analytics spec 3.6); without it there's no id and no ads group. */
export function getInstallId() {
  if (!installId && readConsent() === 'all') {
    installId = String(readJSON(KEYS.installId, '') || '').toLowerCase()
    if (!installId) {
      installId = uuid().toLowerCase()
      writeJSON(KEYS.installId, installId)
    }
  }
  return installId
}

const clamp = (n, min, max, fallback) =>
  typeof n === 'number' && Number.isFinite(n)
    ? Math.min(max, Math.max(min, Math.round(n)))
    : fallback

const isProductionHost = () => PRODUCTION_HOSTS.includes(window.location.hostname)

// Clicks on the test site and on staging don't count (spec 4.5); the config
// can ask for the same on production (phase 1, `web.adtest`).
export const adTest = () => !isProductionHost() || ads?.web?.adtest === true

function loadScript() {
  if (script !== 'none') return
  script = 'loading'
  const add = () => {
    const tag = document.createElement('script')
    tag.async = true
    tag.crossOrigin = 'anonymous'
    tag.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.ADS.CLIENT}`
    tag.onload = () => (script = 'loaded')
    tag.onerror = () => (script = 'failed') // ad blocker
    document.head.appendChild(tag)
  }
  // After the page has shown (spec 4.2).
  if (document.readyState === 'complete') setTimeout(add, 0)
  else window.addEventListener('load', add, { once: true })
}

export const scriptBlocked = () => script === 'failed'

// spec 3.2/3.3: a test browser always, otherwise on + in the rollout group.
function evaluate() {
  const web = ads?.web
  const isTest = (ads?.testInstallIds || []).some((id) => String(id).toLowerCase() === installId)
  const inRollout = bucket >= 0 && bucket < clamp(ads?.rolloutPercent, 0, 100, 0)
  eligible = Boolean(web) && (isTest || (web.enabled === true && inRollout))
  registerProps({ ads_bucket: eligible ? 'on' : 'off' })
  setPersonProps({ ads_bucket: eligible ? 'on' : 'off' })
  if (eligible) loadScript()
}

async function fetchConfig() {
  try {
    const body = await api.config()
    ads = body?.ads || null
    fetchedAt = Date.now()
    writeJSON(KEYS.adsConfig, { ads, at: fetchedAt })
    evaluate()
  } catch (e) {
    // keep the saved config
  }
}

/* Once, before the first render: the saved config right away (so the first
   feed knows its ads), a fresh one in the background, and again when the tab
   comes back after ttlSeconds. */
export function startAds() {
  if (getInstallId()) bucket = rolloutBucket(installId)
  const saved = readJSON(KEYS.adsConfig, null)
  if (saved) {
    ads = saved.ads || null
    fetchedAt = saved.at || 0
  }
  evaluate()
  fetchConfig()
  document.addEventListener('visibilitychange', () => {
    const ttl = clamp(ads?.ttlSeconds, 60, 86400, DEFAULT_TTL)
    if (document.visibilityState === 'visible' && Date.now() - fetchedAt > ttl * 1000) fetchConfig()
  })
  // For the team: the id to put in testInstallIds. Turns nothing on.
  if (new URLSearchParams(window.location.search).get('ads') === 'preview') {
    // eslint-disable-next-line no-console
    console.log(
      installId
        ? `[Instapres] installId: ${installId}`
        : '[Instapres] Nema installId: prvo prihvati kolačiće ("Prihvati sve").'
    )
  }
}

// "Prihvati sve" on the banner: now there's an id, and with it an ads group.
export function adsConsentGiven() {
  if (getInstallId()) bucket = rolloutBucket(installId)
  evaluate()
}

/* The rules for a feed that is loading now, or null for no ads (spec 2.1).
   Taken once per load: a config change shows from the next load. */
export function feedAdRules(surface) {
  const web = ads?.web
  const feed = web?.feed
  if (!eligible || web?.surfaces?.[surface] !== true || !feed) return null
  if (typeof feed.firstAfter !== 'number' || typeof feed.every !== 'number') return null
  if (!(feed.formats || []).includes('mrec') || !web.units?.feedMrec) return null
  const max = clamp(feed.max, 0, 8, 0)
  if (!max) return null
  return {
    firstAfter: clamp(feed.firstAfter, 1, 10, 2),
    every: clamp(feed.every, 2, 10, 4),
    max,
    unit: String(web.units.feedMrec),
  }
}

// The sticky 300×250 in the right column (spec 2.2), or null.
export function sidebarAdUnit() {
  const web = ads?.web
  if (!eligible || web?.surfaces?.sidebar !== true || !web.units?.sidebar) return null
  return String(web.units.sidebar)
}

// The config's surface names → the analytics ones (analytics spec 5).
const EVENT_SURFACE = { myTopics: 'my_topics', search: 'search_results' }

// Parameters of every ad_* event (spec 6).
export const adEventParams = (surface, position, format = 'mrec') => ({
  surface: EVENT_SURFACE[surface] || surface,
  position,
  format,
  adsBucket: eligible ? 'on' : 'off',
})
